// ECharts SVG (ADAPT מ-v1 chart_ec.mjs): רנדרר SVG (ייצוא PNG + קריאת DOM), צבעים מ-CSS tokens
// בזמן הציור (חושך מקבל את הפלטה שלו), ציר מאפס, תוויות RTL עם RLM. האופציה נבנית ב-kit/series.js (משותף ל-iframe).
// סדרות רבות (> 6, או top/show) → חוקר סדרות (kit/explorer.js): גרף + טבלה שהיא המקרא.
import { BlockHead } from './Expand.tsx';
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent, AriaComponent, MarkLineComponent, MarkPointComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { ChartBlock as ChartBlockT, ColType } from '@aibify/core';
import { fmtAxis, fmtCell } from './format.ts';
import { downloadBlob, svgToPng } from './png.ts';
import { useDisplay } from './context.tsx';
import { S } from './strings.ts';
import { isExplorer, missingColumnsHe, palette, pieSlices } from './kit/series.js';
import { FONT, buildOption, neededColumns, pieLayout, type ChartTokens } from './chart-option.ts';
import { createExplorer, type Explorer } from './kit/explorer.js';

export { buildOption, type ChartTokens };

echarts.use([BarChart, LineChart, PieChart, GridComponent, LegendComponent, TooltipComponent, AriaComponent, MarkLineComponent, MarkPointComponent, SVGRenderer]);

/** 24 §1.1: בדף התצוגה של אייבי (html[data-ab-preview]) — בלי הנפשה (גם עוגה), הצילום מיידי. */
const still = <T extends object>(o: T): T => (document.documentElement.hasAttribute('data-ab-preview') ? { ...o, animation: false } : o);

function isDark(): boolean {
  const el = document.documentElement;
  if (el.dataset.theme) return el.dataset.theme === 'dark';
  return el.classList.contains('dark') || window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** tokens מה-CSS של הדף, בזמן הציור. פלטה של 12: 6 המותג (--chart-1..6) + 6 משלימים לערכה. */
export function chartTokens(): ChartTokens {
  const cs = getComputedStyle(document.documentElement);
  const raw = (n: string) => cs.getPropertyValue(n).trim();
  const v = (n: string, fb: string, alpha?: number) => {
    const x = raw(n);
    return x ? (alpha === undefined ? `hsl(${x})` : `hsl(${x} / ${alpha})`) : fb;
  };
  const dark = isDark();
  const brand = [1, 2, 3, 4, 5, 6].map((i) => raw(`--chart-${i}`)).filter(Boolean).map((x) => `hsl(${x})`);
  return {
    palette: palette(brand, dark),
    fg: v('--foreground', '#111'),
    muted: v('--muted-foreground', '#666'),
    border: v('--border', '#ddd'),
    grid: v('--border', '#ddd', 0.7),
    card: v('--card', '#fff'),
    pointer: v('--muted-foreground', '#666', 0.08),
    font: FONT,
    dark,
  };
}

function useThemeRedraw(draw: () => void, deps: unknown[]) {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', draw);
    const mo = new MutationObserver(draw);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => {
      mq.removeEventListener('change', draw);
      mo.disconnect();
    };
  }, deps);
}

function ClassicChart({ block }: { block: ChartBlockT }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // עוגה צרה (טלפון): המקרא עובר מתחת לעוגה והמיכל גדל לפי מספר הפרוסות (pieLayout)
    const pieN = block.chartType === 'pie' ? pieSlices(block.rows, block.x, block.y[0]!, 8).length : 0;
    let width = el.clientWidth;
    const fit = () => {
      const l = pieN ? pieLayout(width, pieN) : null;
      el.style.blockSize = l ? `${l.height}px` : '';
      el.style.maxBlockSize = l ? 'none' : ''; // עוגה: הגובה לפי מספר הפרוסות, לא תקרת 16:7
    };
    fit();
    const chart = echarts.init(el, undefined, { renderer: 'svg' });
    chartRef.current = chart;
    chart.setOption(still(buildOption(block, chartTokens(), null, width)), true);
    // מעבר על סדרה → היא בולטת והשאר מתעמעמים (ההדגשה המובנית של ECharts מעלימה ב-SVG את הקו המודגש)
    let focus: string | null = null;
    const setFocus = (name: string | null) => {
      if (name === focus) return;
      focus = name;
      chart.setOption(still(buildOption(block, chartTokens(), focus, width)));
    };
    chart.on('mouseover', (p: { componentType?: string; seriesName?: string }) => {
      if (p.componentType === 'series' && p.seriesName) setFocus(p.seriesName);
    });
    chart.on('mouseout', () => setFocus(null));
    chart.on('globalout', () => setFocus(null));
    const ro = new ResizeObserver(() => {
      if (pieN && el.clientWidth !== width) {
        width = el.clientWidth;
        fit();
        chart.resize();
        chart.setOption(still(buildOption(block, chartTokens(), focus, width)), true);
      } else chart.resize();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [block]);
  useThemeRedraw(() => chartRef.current?.setOption(still(buildOption(block, chartTokens(), null, ref.current?.clientWidth)), true), [block]);
  return <div ref={ref} className="ab-chart" data-testid="chart" role="img" aria-label={block.titleHe ?? S.chart} />;
}

const cf0 = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 });
/** פורמט לסטטיסטיקות בטבלת המקרא: סכום שלם בשקלים בלי ".00" (הסכומים מעוגלים ממילא); השאר כמו בתאים. */
const statFmt = (v: unknown, type: string) => (type === 'currency' && typeof v === 'number' && Number.isInteger(v) ? cf0.format(v) : fmtCell(v, type as ColType));

function ExplorerChart({ block }: { block: ChartBlockT }) {
  const ref = useRef<HTMLDivElement>(null);
  const ex = useRef<Explorer | null>(null);
  const mount = (keep?: { visible: string[]; highlight: string[] }) => {
    const el = ref.current;
    if (!el) return;
    ex.current?.destroy();
    const unit = block.unit ?? 'number';
    ex.current = createExplorer(
      el,
      {
        rows: block.rows,
        x: block.x,
        y: block.y,
        seriesBy: block.seriesBy,
        chartType: block.chartType,
        stacked: block.stacked,
        unit,
        top: block.top,
        show: keep ? keep.visible : block.show,
        highlight: keep ? keep.highlight : block.highlight,
        marks: block.marks,
        series: block.series,
        stats: block.stats,
        titleHe: block.titleHe,
      },
      { echarts, t: chartTokens(), fmt: statFmt, axisFmt: fmtAxis },
    );
  };
  useEffect(() => {
    mount();
    return () => {
      ex.current?.destroy();
      ex.current = null;
    };
  }, [block]);
  // החלפת ערכה: בונים מחדש עם הצבעים החדשים, ושומרים את מה שהמשתמש בחר
  useThemeRedraw(() => mount(ex.current?.state()), [block]);
  return <div ref={ref} className="ab-explorer" data-testid="chart" />;
}

export function ChartView({ block }: { block: ChartBlockT }) {
  const missing = missingColumnsHe(block.rows, neededColumns(block));
  if (missing)
    return (
      <p className="ab-error" role="alert">
        {missing}
      </p>
    );
  return isExplorer(block) ? <ExplorerChart block={block} /> : <ClassicChart block={block} />;
}

export function useChartPng(block: ChartBlockT, host: React.RefObject<HTMLElement | null>) {
  const { asOfText } = useDisplay();
  return async () => {
    const svg = host.current?.querySelector('.ab-chart svg, .abx-chart svg');
    if (!(svg instanceof SVGSVGElement)) return;
    downloadBlob(await svgToPng(svg, { title: block.titleHe, stamp: asOfText }), `${block.titleHe ?? block.id}.png`);
  };
}

export function ChartBlock({ block }: { block: ChartBlockT }) {
  const host = useRef<HTMLElement>(null);
  const png = useChartPng(block, host);
  return (
    <section ref={host} className="ab-block" data-block-type="chart" data-block-id={block.id} data-explorer={isExplorer(block) || undefined}>
      <BlockHead titleHe={block.titleHe}>
        <button type="button" className="ab-btn ab-btn-ghost" onClick={png}>
          {S.exportPng}
        </button>
      </BlockHead>
      <ChartView block={block} />
    </section>
  );
}
